/**
 * Consent plugin: resolves host consent, renders the built-in chooser when
 * ProsPix owns the decision, and records consent UI interactions.
 */
import { readStoredConsent, resolveConsent } from "../lib/consent";
import { recordConsentResponse } from "../lib/consent-response";
import { consentStyles } from "../lib/consent-styles";
import {
  readConsentHostTheme,
  resolveConsentTheme,
} from "../lib/consent-theme";
import {
  resolveConsentUiVisibility,
  sanitizePrivacyUrl,
} from "../lib/consent-ui";
import { isActiveTestEnrollment } from "../lib/test-enrollment";
import type {
  ConsentSnapshot,
  ConsentUiMode,
  PluginContext,
} from "../lib/types";

import { registerPlugin } from "./register";

const CONSENT_ROOT_ID = "prospix-consent-root";
const OPEN_CONSENT_EVENT = "prospix:open-consent";

const sameArray = (a?: number[], b?: number[]) => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) return false;
  }
  return true;
};

export async function reconcileHostConsent(
  ctx: PluginContext,
  transition?: { transitionVersion: 1; occurredAt: number },
) {
  const consentKey = `prospix_consent_${ctx.siteKey || "default"}`;
  const stored = readStoredConsent(ctx.storage.get(consentKey));
  const snapshot = await resolveConsent(ctx.config, stored);
  const current = ctx.consent;
  const changed =
    snapshot.state !== current.state ||
    snapshot.gpc !== current.gpc ||
    snapshot.dnt !== current.dnt ||
    snapshot.cmp?.tcString !== current.cmp?.tcString ||
    snapshot.cmp?.uspString !== current.cmp?.uspString ||
    snapshot.cmp?.gppString !== current.cmp?.gppString ||
    !sameArray(snapshot.cmp?.gppSectionIds, current.cmp?.gppSectionIds);
  if (changed) {
    return transition
      ? ctx.reconcileConsent(snapshot, transition)
      : ctx.reconcileConsent(snapshot);
  }
  return current;
}

function isShopifySite() {
  const shopify = (window as any).Shopify;
  return Boolean(
    window.location.hostname.endsWith(".myshopify.com") ||
    shopify?.customerPrivacy ||
    shopify?.shop ||
    shopify?.routes?.root,
  );
}

function hasHostConsentManager() {
  const win = window as any;
  if (
    typeof win.__tcfapi === "function" ||
    typeof win.__gpp === "function" ||
    win.OneTrust ||
    win.Cookiebot ||
    win.Didomi ||
    win.Osano
  ) {
    return true;
  }

  try {
    if (
      document.querySelector(
        ["#onetrust-banner-sdk", "#CybotCookiebotDialog", "#didomi-host"].join(
          ",",
        ),
      )
    ) {
      return true;
    }

    return Boolean(
      document.querySelector(
        "[data-nosnippet='true'][role='dialog'][aria-label*='cookie' i]",
      ),
    );
  } catch {
    return false;
  }
}

export function createConsentPlugin(ctx: PluginContext) {
  const pageStart = Date.now();
  let rootHost: HTMLElement | null = null;
  let forceOpen = false;
  let answeredOnPage = false;
  let previewDismissed = false;
  const previewDismissedKey = `prospix_notice_dismissed_v1_${ctx.siteKey || "default"}`;
  try {
    previewDismissed = ctx.storage.get(previewDismissedKey) === "1";
  } catch {
    // A blocked storage adapter must not prevent the notice from working.
  }
  let initialTimer: ReturnType<typeof setTimeout> | undefined;
  let managerRecheckTimer: ReturnType<typeof setTimeout> | undefined;
  let managerObserver: MutationObserver | undefined;
  let unsubscribeConsent: (() => void) | undefined;

  const removeUi = () => {
    rootHost?.remove();
    rootHost = null;
  };

  const trackInteraction = (action: string, stateBefore: string) => {
    if (!ctx.canTrackCategory("consent_analytics")) return;
    ctx.track("consent_banner_interaction", {
      action,
      cmp_source: "prospix_ui",
      latency_ms: Date.now() - pageStart,
      state_before: stateBefore,
    });
  };

  const getUiConfig = () => {
    const config = ctx.config.privacy.consentUi;
    return {
      mode: config?.mode ?? ("auto" as ConsentUiMode),
      privacyUrl: sanitizePrivacyUrl(config?.privacyUrl, window.location.href),
      showPreferencesButton: config?.showPreferencesButton !== false,
      theme: resolveConsentTheme(
        config?.theme,
        config?.adoptHostStyles === false ? {} : readConsentHostTheme(),
      ),
    };
  };

  const createButton = (
    label: string,
    kind: "primary" | "secondary" | "deny" | "preferences" | "dismiss",
    onClick: () => void,
  ) => {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = label;
    button.className = `prospix-consent-${kind}`;
    button.addEventListener("click", onClick);
    return button;
  };

  const renderUi = (snapshot: ConsentSnapshot = ctx.consent) => {
    const uiConfig = getUiConfig();
    const enrolledTest = isActiveTestEnrollment(snapshot);
    const preview =
      enrolledTest || ctx.config.privacy.consentEnforcement === "preview";
    if (preview && document.querySelector("[data-prospix-consent-banner]")) {
      removeUi();
      return;
    }
    let decisionState: ConsentSnapshot["state"] | undefined;
    if (!preview && !answeredOnPage) {
      try {
        decisionState = readStoredConsent(
          ctx.storage.get(`prospix_consent_${ctx.siteKey || "default"}`),
        )?.state;
      } catch {
        // Unavailable storage cannot establish an unanswered persisted choice.
      }
    }
    const visibility = preview
      ? previewDismissed && !forceOpen
        ? "preferences"
        : "prompt"
      : resolveConsentUiVisibility({
          state: snapshot.state,
          decisionState,
          mode: uiConfig.mode,
          gpc: snapshot.gpc,
          dnt: snapshot.dnt,
          isShopify: isShopifySite(),
          hasHostCmp: hasHostConsentManager(),
          forceOpen,
          showPreferencesButton: uiConfig.showPreferencesButton,
        });

    if (visibility === "hidden") {
      removeUi();
      return;
    }

    const renderMode = enrolledTest
      ? "enrolled-test"
      : preview
        ? "preview"
        : "required";
    if (
      rootHost?.isConnected &&
      rootHost.dataset.prospixConsentUi === visibility &&
      rootHost.dataset.prospixConsentMode === renderMode
    ) {
      return;
    }

    const restoreFocus = Boolean(rootHost?.shadowRoot?.activeElement);
    removeUi();
    rootHost = document.createElement("div");
    rootHost.id = CONSENT_ROOT_ID;
    rootHost.dataset.prospixConsentUi = visibility;
    // Rebuild the copy and choice handlers when an ordinary prompt becomes
    // an agreed test. Identical visibility must not retain real-choice closures.
    rootHost.dataset.prospixConsentMode = renderMode;
    rootHost.dataset.prospixConsentEnforcement = preview
      ? "preview"
      : "required";
    document.body.appendChild(rootHost);

    const shadow = rootHost.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = consentStyles;
    shadow.appendChild(style);

    const shell = document.createElement("div");
    shell.className = "prospix-consent-shell";
    shell.style.setProperty(
      "--prospix-consent-font",
      uiConfig.theme.fontFamily,
    );
    shell.style.setProperty(
      "--prospix-consent-background",
      uiConfig.theme.backgroundColor,
    );
    shell.style.setProperty("--prospix-consent-text", uiConfig.theme.textColor);
    shell.style.setProperty(
      "--prospix-consent-accent",
      uiConfig.theme.accentColor,
    );
    shadow.appendChild(shell);

    if (visibility === "preferences") {
      shell.classList.add("prospix-consent-shell--preferences");
      const reopen = createButton("", "preferences", () => {
        if (!preview) trackInteraction("customize", ctx.consent.state);
        forceOpen = true;
        renderUi(ctx.consent);
        rootHost?.shadowRoot
          ?.querySelector<HTMLElement>(".prospix-consent-panel")
          ?.focus();
      });
      reopen.setAttribute("aria-label", "Privacy choices");
      reopen.title = "Privacy choices";
      const icon = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "svg",
      );
      icon.setAttribute("viewBox", "0 0 24 24");
      icon.setAttribute("aria-hidden", "true");
      icon.setAttribute("focusable", "false");
      icon.setAttribute("fill", "none");
      icon.setAttribute("stroke", "currentColor");
      icon.setAttribute("stroke-width", "1.8");
      const path = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path.setAttribute(
        "d",
        "M12 3 4 6v5c0 5 8 10 8 10s8-5 8-10V6l-8-3Z M9 12l2 2 4-4",
      );
      icon.appendChild(path);
      reopen.appendChild(icon);
      shell.appendChild(reopen);
      if (restoreFocus) reopen.focus();
      return;
    }

    const panel = document.createElement("section");
    panel.className = "prospix-consent-panel";
    panel.tabIndex = -1;
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-modal", "false");
    panel.setAttribute("aria-labelledby", "prospix-consent-title");
    panel.setAttribute("aria-describedby", "prospix-consent-copy");

    const dismissPreview = () => {
      previewDismissed = true;
      try {
        ctx.storage.set(previewDismissedKey, "1");
      } catch {
        // Closing this page's notice still works without durable storage.
      }
      forceOpen = false;
      renderUi(ctx.consent);
      rootHost?.shadowRoot
        ?.querySelector<HTMLButtonElement>(".prospix-consent-preferences")
        ?.focus();
    };

    if (preview) {
      const dismiss = createButton("×", "dismiss", dismissPreview);
      dismiss.setAttribute("aria-label", "Close notice");
      panel.appendChild(dismiss);
    }

    const title = document.createElement("h2");
    title.id = "prospix-consent-title";
    title.className = "prospix-consent-title";
    title.textContent = enrolledTest
      ? "Test privacy choices"
      : preview
        ? "Privacy"
        : "Privacy choices";
    panel.appendChild(title);

    const copy = document.createElement("p");
    copy.id = "prospix-consent-copy";
    copy.className = "prospix-consent-copy";
    const personalizationAvailable =
      ctx.config.identityCapture.enabled &&
      ctx.config.identityCapture.purposes.length > 0;
    copy.textContent = enrolledTest
      ? "Your answer is recorded for this website test. Use Stop test to end activity and contact capture."
      : preview
        ? snapshot.state === "denied"
          ? "Collection is blocked by a browser privacy signal."
          : "This site collects visit activity and details submitted through forms."
        : personalizationAvailable
          ? "Analytics helps us understand visits and improve this site. Personalization also links details you submit, such as your email, with your activity to tailor marketing. Basic visit counts continue without identity or behavior details."
          : "We use optional analytics to understand visits and improve this site. Basic visit counts continue without identity or behavior details.";
    panel.appendChild(copy);

    if (uiConfig.privacyUrl) {
      const link = document.createElement("a");
      link.className = "prospix-consent-link";
      link.href = uiConfig.privacyUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = "Privacy policy";
      panel.appendChild(link);
    }

    const choose = (state: "granted" | "denied", personalization = false) => {
      const recordResponse = () =>
        recordConsentResponse(
          ctx,
          state === "denied"
            ? "denied"
            : personalization
              ? "personalization"
              : "analytics",
          "prospix_ui",
        );
      if (preview) {
        recordResponse();
        dismissPreview();
        return;
      }
      const stateBefore = ctx.consent.state;
      // An explicit choice closes this page's prompt even while the real
      // asynchronous transition is pending or storage is unavailable.
      answeredOnPage = true;
      forceOpen = false;
      const marketing =
        state === "granted" && personalizationAvailable && personalization;
      const purposes = {
        version: 1 as const,
        analytics: state === "granted",
        marketing,
      };
      // Older built-in choices are permanently analytics-only. Use distinct
      // provenance only for the new, explicit personalization action.
      const source = marketing ? "prospix_ui_personalization_v1" : "prospix_ui";
      const transition = (window as any).prospix?.consent({
        state,
        cmp: { source, purposes },
        transitionVersion: 1,
        occurredAt: Date.now(),
      });
      const recordAnswer = () => {
        recordResponse();
        trackInteraction(
          state === "granted" ? "accept" : "reject",
          stateBefore,
        );
      };
      void Promise.resolve(transition).then(recordAnswer, recordAnswer);
      renderUi({
        ...ctx.consent,
        state,
        cmp: { source, purposes },
      });
    };

    const actions = document.createElement("div");
    actions.className = "prospix-consent-actions";
    const options = document.createElement("div");
    options.className = "prospix-consent-options";
    options.hidden = true;
    let savedChoice: unknown;
    if (preview) {
      try {
        savedChoice = JSON.parse(
          ctx.storage.get(
            `prospix_ui_response_v1_${ctx.siteKey || "default"}`,
          ) || "null",
        )?.choice;
      } catch {
        /* No previous test answer. */
      }
    }
    const option = (label: string, checked: boolean) => {
      const row = document.createElement("label");
      row.className = "prospix-consent-option";
      row.appendChild(document.createTextNode(label));
      const input = document.createElement("input");
      input.type = "checkbox";
      input.checked = checked;
      row.appendChild(input);
      options.appendChild(row);
      return input;
    };
    const analytics = option(
      "Analytics",
      preview
        ? savedChoice === "analytics" || savedChoice === "personalization"
        : snapshot.cmp?.purposes?.analytics === true,
    );
    const personalization = personalizationAvailable
      ? option(
          "Personalization",
          preview
            ? savedChoice === "personalization"
            : snapshot.cmp?.purposes?.marketing === true,
        )
      : null;
    if (personalization) personalization.disabled = !analytics.checked;
    analytics.addEventListener("change", () => {
      if (personalization) {
        personalization.disabled = !analytics.checked;
        if (!analytics.checked) personalization.checked = false;
      }
    });
    const optionActions = document.createElement("div");
    optionActions.className = "prospix-consent-actions";
    const settings = createButton("Settings", "secondary", () => {
      actions.hidden = true;
      options.hidden = false;
      analytics.focus();
    });
    optionActions.appendChild(
      createButton("Back", "secondary", () => {
        options.hidden = true;
        actions.hidden = false;
        settings.focus();
      }),
    );
    optionActions.appendChild(
      createButton("Save choices", "primary", () =>
        choose(
          analytics.checked ? "granted" : "denied",
          personalization?.checked === true,
        ),
      ),
    );
    options.appendChild(optionActions);
    panel.appendChild(options);
    actions.appendChild(settings);
    actions.appendChild(createButton("Deny", "deny", () => choose("denied")));
    actions.appendChild(
      createButton("Accept all", "primary", () =>
        choose("granted", personalizationAvailable),
      ),
    );
    panel.appendChild(actions);
    shell.appendChild(panel);
    if (restoreFocus) panel.focus();
  };

  const refresh = async (transition?: {
    transitionVersion: 1;
    occurredAt: number;
  }) => {
    const snapshot = await reconcileHostConsent(ctx, transition);
    renderUi(snapshot);
  };

  const selectorActionMap: Array<{ selector: string; action: string }> = [
    { selector: "#onetrust-accept-btn-handler", action: "accept" },
    { selector: "#onetrust-reject-all-handler", action: "reject" },
    { selector: "#onetrust-pc-btn-handler", action: "customize" },
    { selector: "[data-consent-action='accept']", action: "accept" },
    { selector: "[data-consent-action='reject']", action: "reject" },
    { selector: "[data-consent-action='save']", action: "save" },
    { selector: "[data-cookie-action='accept']", action: "accept" },
    { selector: "[data-cookie-action='reject']", action: "reject" },
    { selector: "[data-cookie-action='save']", action: "save" },
  ];

  const onBannerClick = (event: Event) => {
    if (!ctx.canTrackCategory("consent_analytics")) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const match = selectorActionMap.find(({ selector }) =>
      Boolean(target.closest(selector)),
    );
    if (!match) return;

    ctx.track("consent_banner_interaction", {
      action: match.action,
      cmp_source: ctx.consent.cmp?.source,
      latency_ms: Date.now() - pageStart,
      state_before: ctx.consent.state,
    });
  };

  const onOpenConsent = () => {
    forceOpen = true;
    renderUi(ctx.consent);
    rootHost?.shadowRoot
      ?.querySelector<HTMLElement>(".prospix-consent-panel")
      ?.focus();
  };
  const onConsentPolicy = () => renderUi(ctx.consent);

  const onNoticeStorage = (event: StorageEvent) => {
    if (event.key !== previewDismissedKey && event.key !== null) return;
    previewDismissed = event.newValue === "1";
    if (
      isActiveTestEnrollment(ctx.consent) ||
      ctx.config.privacy.consentEnforcement === "preview"
    ) {
      forceOpen = false;
      renderUi(ctx.consent);
    }
  };

  return {
    onInit() {
      initialTimer = setTimeout(() => {
        void refresh();
      }, 250);

      managerRecheckTimer = setTimeout(() => {
        renderUi(ctx.consent);
      }, 1500);

      if (typeof MutationObserver !== "undefined") {
        managerObserver = new MutationObserver(() => {
          if (
            rootHost &&
            ((ctx.config.privacy.consentEnforcement === "preview" &&
              document.querySelector("[data-prospix-consent-banner]")) ||
              (getUiConfig().mode === "auto" && hasHostConsentManager()))
          ) {
            renderUi(ctx.consent);
          }
        });
        managerObserver.observe(document.documentElement, {
          childList: true,
          subtree: true,
        });
      }

      const tcfapi = (window as any)?.__tcfapi;
      if (tcfapi) {
        try {
          tcfapi("addEventListener", 2, (tcData: unknown, success: boolean) => {
            const record =
              tcData && typeof tcData === "object"
                ? (tcData as Record<string, unknown>)
                : undefined;
            void refresh(
              success && record?.eventStatus === "useractioncomplete"
                ? { transitionVersion: 1, occurredAt: Date.now() }
                : undefined,
            );
          });
        } catch {
          // ignore
        }
      }

      const gpp = (window as any)?.__gpp;
      if (gpp) {
        try {
          gpp("addEventListener", () => {
            void refresh();
          });
        } catch {
          // ignore
        }
      }

      unsubscribeConsent = ctx.on("consent", (payload) => {
        const snapshot = payload as ConsentSnapshot;
        renderUi(snapshot);
      });
      window.addEventListener(OPEN_CONSENT_EVENT, onOpenConsent);
      window.addEventListener("prospix:consent-policy", onConsentPolicy);
      window.addEventListener("storage", onNoticeStorage);
      document.addEventListener("click", onBannerClick, { capture: true });
    },
    onUnload() {
      if (initialTimer) clearTimeout(initialTimer);
      if (managerRecheckTimer) clearTimeout(managerRecheckTimer);
      managerObserver?.disconnect();
      unsubscribeConsent?.();
      window.removeEventListener(OPEN_CONSENT_EVENT, onOpenConsent);
      window.removeEventListener("prospix:consent-policy", onConsentPolicy);
      window.removeEventListener("storage", onNoticeStorage);
      document.removeEventListener("click", onBannerClick, true);
      removeUi();
    },
  };
}

registerPlugin("consent", createConsentPlugin);
